import { Injectable, OnDestroy, Renderer2, RendererFactory2 } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, ReplaySubject, Subject, takeUntil } from 'rxjs';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import { notUndefined } from 'app/utils/stream-util';

import { SideContainerType } from '../social-menu/enums/SideContainerType';
import { SubMenu, CHAT_SUB_MENU } from '../social-menu/social-menu.types';
import { socialAppSelectors } from '../state/app';

import { BrowserService } from './browser.service';

@Injectable({
  providedIn: 'root',
})
export class SideSectionService extends Loggable implements OnDestroy {
  public openStateChanged$ = new Subject<boolean>();
  public sideContainerType: SideContainerType = SideContainerType.NONE;
  private renderer2: Renderer2;

  private currentSubMenus: SubMenu[] = [];
  public subMenu$: BehaviorSubject<SubMenu[]> = new BehaviorSubject<SubMenu[]>([]);
  private destroyed$ = new ReplaySubject<boolean>(1);
  private isHost = false;

  // Customer API
  private setGuestMenu = false;
  private guestMenuState?: SideContainerType;

  public constructor(
    private rendererFactory: RendererFactory2,
    private browserService: BrowserService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('SideSectionService');
    this.renderer2 = this.rendererFactory.createRenderer(null, null);

    // checks for isHost when opening the action menu when joining a room
    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    // Customer API Side Menu Settings
    this.store
      .select(socialAppSelectors.setGuestMenuState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestMenu = state;
      });

    this.store
      .select(socialAppSelectors.getGuestMenuState)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.info('Guest Menu State applied', setting);
        this.guestMenuState = setting;
      });
    // checks for the social id when opening the action menu when joining a room
    this.store
      .select(socialAppSelectors.getJoinedSocialId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((socialId) => this.handleJoinedSocial(socialId));

    this.subMenu$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (newSubmenus) => {
        this.currentSubMenus = newSubmenus;
        // fire resize event to resize canvas
        window.dispatchEvent(new Event('resize'));
      },
    });
  }

  public setOpenState(sideContainerType: SideContainerType): void {
    this.sideContainerType = sideContainerType;
    this.openStateChanged$.next(sideContainerType !== SideContainerType.NONE);
    if (this.sideContainerType !== SideContainerType.NONE) {
      this.renderer2.addClass(document.body, 'menu-open');
      this.resetSubMenus();
    } else {
      this.renderer2.removeClass(document.body, 'menu-open');
    }
  }

  // Open Action menu when host joins social
  private handleJoinedSocial(socialId?: string): void {
    if (socialId) {
      // Set guest menu state
      if (this.setGuestMenu) {
        if (this.guestMenuState === SideContainerType.CHAT) {
          this.openSubMenu(CHAT_SUB_MENU);
        } else {
          this.setOpenState(this.guestMenuState ?? SideContainerType.NONE);
        }
      }
      // Set host state
      if (this.isHost && !this.browserService.isTabletOrMobileBreakpoint()) {
        this.setOpenState(SideContainerType.ACTION_MENU);
      }
      this.setOpenState(SideContainerType.NONE);
    }
  }

  /**
   * Return the menu state to the main (ACTION) menu
   */
  public openMainMenu(): void {
    this.setOpenState(SideContainerType.ACTION_MENU);
    // fire resize event to resize canvas
    window.dispatchEvent(new Event('resize'));
  }

  public openSubMenu(subMenu: SubMenu): void {
    if (this.sideContainerType === SideContainerType.NONE) {
      this.openMainMenu();
    }
    const updatedCurrentSubMenus = [...this.currentSubMenus, subMenu];
    this.subMenu$.next(updatedCurrentSubMenus);
  }
  public addSubMenu(subMenu: SubMenu): void {
    const newSubMenuArray: SubMenu[] = this.currentSubMenus;
    newSubMenuArray.push(subMenu);
    this.subMenu$.next(newSubMenuArray);
  }

  public popSubMenu(): void {
    this.currentSubMenus.pop();
    this.subMenu$.next(this.currentSubMenus);
  }

  public resetSubMenus(): void {
    this.currentSubMenus = [];
    this.subMenu$.next([]);
  }

  public getOpenSubMenu(): SubMenu | undefined {
    const totalSubMenus: number = this.currentSubMenus.length | 0;
    const lastSubmenuOfList = totalSubMenus ? this.currentSubMenus[totalSubMenus - 1] : undefined;
    return lastSubmenuOfList;
  }

  public closeAllMenus(): void {
    this.resetSubMenus();
    this.setOpenState(SideContainerType.NONE);
  }

  public ngOnDestroy(): void {
    this.renderer2.removeClass(document.body.parentElement, 'menu-open');
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.openStateChanged$.complete();
    this.subMenu$.complete();
  }
}
